import { Plus, Trash2, ChevronUp, ChevronDown, Save } from "lucide-react";
import { useCrud } from "./useCrud";
import { Field, ImageField } from "./ImageField";
import { GUIDE_ICON_NAMES } from "@/lib/guideIcons";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const Actions = ({ onSave, onUp, onDown, onDelete }) => (
  <div className="flex items-center gap-2">
    <button onClick={onSave} className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal/90 text-obsidian px-4 py-2 hover:bg-teal"><Save className="h-4 w-4" /> Save</button>
    <button onClick={onUp} className="p-2 border border-[#2A201A] hover:border-teal hover:text-teal transition-colors"><ChevronUp className="h-4 w-4" /></button>
    <button onClick={onDown} className="p-2 border border-[#2A201A] hover:border-teal hover:text-teal transition-colors"><ChevronDown className="h-4 w-4" /></button>
    <button onClick={onDelete} className="ml-auto p-2 border border-[#2A201A] text-[#8F877C] hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
  </div>
);

const StepsBlock = () => {
  const { items, patch, add, save, remove, move } = useCrud("guide-steps", { text: "New step" });
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal">Getting Started</p>
        <button onClick={add} data-testid="guide-step-add" className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest border border-[#2A201A] px-3 py-2 hover:border-teal hover:text-teal transition-colors"><Plus className="h-4 w-4" /> Add step</button>
      </div>
      {items.map((it, idx) => (
        <div key={it.id} className="border border-[#2A201A] bg-surface/40 p-4 space-y-3">
          <Field label={`Step ${idx + 1}`} value={it.text} onChange={(v) => patch(it.id, "text", v)} />
          <Actions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};

const KeybindsBlock = () => {
  const { items, patch, add, save, remove, move } = useCrud("keybinds", { key: "F", action: "Action" });
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal">Keybinds &amp; Controls</p>
        <button onClick={add} data-testid="keybind-add" className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest border border-[#2A201A] px-3 py-2 hover:border-teal hover:text-teal transition-colors"><Plus className="h-4 w-4" /> Add key</button>
      </div>
      {items.map((it, idx) => (
        <div key={it.id} className="border border-[#2A201A] bg-surface/40 p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-[120px_1fr]">
            <Field label="Key" value={it.key} onChange={(v) => patch(it.id, "key", v)} />
            <Field label="Action" value={it.action} onChange={(v) => patch(it.id, "action", v)} />
          </div>
          <Actions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};

const FaqBlock = () => {
  const { items, patch, add, save, remove, move } = useCrud("faq", { question: "New question?", answer: "Answer" });
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="font-feature text-xs uppercase tracking-[0.25em] text-teal">New Player FAQ</p>
        <button onClick={add} data-testid="faq-add" className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest border border-[#2A201A] px-3 py-2 hover:border-teal hover:text-teal transition-colors"><Plus className="h-4 w-4" /> Add FAQ</button>
      </div>
      {items.map((it, idx) => (
        <div key={it.id} className="border border-[#2A201A] bg-surface/40 p-4 space-y-3">
          <Field label="Question" value={it.question} onChange={(v) => patch(it.id, "question", v)} />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Answer</p>
            <textarea value={it.answer} onChange={(e) => patch(it.id, "answer", e.target.value)} rows={2}
              className="w-full bg-obsidian border border-[#2A201A] px-3 py-2 text-sm text-bone focus:outline-none focus:border-teal resize-none" />
          </div>
          <Actions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
        </div>
      ))}
    </div>
  );
};

const BlockEditor = ({ block, onChange, onUp, onDown, onDelete }) => (
  <div className="border border-[#2A201A] bg-obsidian/40 p-4 space-y-3">
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-widest text-teal">{block.type} block</span>
      <div className="flex items-center gap-1.5">
        <button onClick={onUp} className="p-1.5 border border-[#2A201A] hover:border-teal hover:text-teal transition-colors"><ChevronUp className="h-3.5 w-3.5" /></button>
        <button onClick={onDown} className="p-1.5 border border-[#2A201A] hover:border-teal hover:text-teal transition-colors"><ChevronDown className="h-3.5 w-3.5" /></button>
        <button onClick={onDelete} className="p-1.5 border border-[#2A201A] text-[#8F877C] hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
    </div>
    {block.type === "text" && (
      <>
        <Field label="Heading (optional)" value={block.heading} onChange={(v) => onChange({ ...block, heading: v })} />
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Body</p>
          <textarea value={block.body || ""} onChange={(e) => onChange({ ...block, body: e.target.value })} rows={4}
            className="w-full bg-obsidian border border-[#2A201A] px-3 py-2 text-sm text-bone focus:outline-none focus:border-teal resize-none" />
        </div>
      </>
    )}
    {block.type === "image" && (
      <>
        <ImageField label="Image" value={block.url} onChange={(v) => onChange({ ...block, url: v })} />
        <Field label="Caption (optional)" value={block.caption} onChange={(v) => onChange({ ...block, caption: v })} />
      </>
    )}
    {block.type === "video" && (
      <>
        <Field label="Video URL (YouTube, Vimeo or .mp4)" value={block.url} onChange={(v) => onChange({ ...block, url: v })} placeholder="https://youtube.com/watch?v=…" />
        <Field label="Caption (optional)" value={block.caption} onChange={(v) => onChange({ ...block, caption: v })} />
      </>
    )}
  </div>
);

const GuideCard = ({ it, idx, patch, setItem, save, remove, move }) => {
  const blocks = it.blocks || [];
  const setBlocks = (next) => setItem(it.id, { blocks: next });
  const addBlock = (type) => setBlocks([...blocks, type === "text" ? { type, heading: "", body: "" } : { type, url: "", caption: "" }]);
  const changeBlock = (bi, val) => setBlocks(blocks.map((b, i) => (i === bi ? val : b)));
  const moveBlock = (bi, dir) => {
    const j = bi + dir; if (j < 0 || j >= blocks.length) return;
    const next = [...blocks]; [next[bi], next[j]] = [next[j], next[bi]]; setBlocks(next);
  };
  const removeBlock = (bi) => setBlocks(blocks.filter((_, i) => i !== bi));
  return (
    <div data-testid={`guide-article-edit-${idx}`} className="border border-[#2A201A] bg-surface/40 p-5 space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title" value={it.title} onChange={(v) => setItem(it.id, { title: v, slug: it.slug || slugify(v) })} />
        <Field label="Slug (URL)" value={it.slug} onChange={(v) => patch(it.id, "slug", slugify(v))} />
      </div>
      <Field label="Short blurb (card)" value={it.blurb} onChange={(v) => patch(it.id, "blurb", v)} />
      <div className="grid gap-4 sm:grid-cols-[1fr_220px] items-end">
        <ImageField label="Cover image (optional)" value={it.cover} onChange={(v) => patch(it.id, "cover", v)} />
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C] mb-2">Icon</p>
          <select value={it.icon || "BookOpen"} onChange={(e) => patch(it.id, "icon", e.target.value)}
            className="w-full bg-obsidian border border-[#2A201A] px-3 py-2.5 text-sm text-bone focus:outline-none focus:border-teal">
            {GUIDE_ICON_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-3 border-t border-[#2A201A] pt-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-[#8F877C]">Content blocks</p>
        {blocks.map((b, bi) => (
          <BlockEditor key={bi} block={b}
            onChange={(v) => changeBlock(bi, v)} onUp={() => moveBlock(bi, -1)} onDown={() => moveBlock(bi, 1)} onDelete={() => removeBlock(bi)} />
        ))}
        <div className="flex flex-wrap gap-2">
          <button onClick={() => addBlock("text")} data-testid={`guide-add-text-${idx}`} className="inline-flex items-center gap-1.5 font-feature text-xs uppercase tracking-widest border border-[#2A201A] px-3 py-2 hover:border-teal hover:text-teal transition-colors"><Plus className="h-3.5 w-3.5" /> Text</button>
          <button onClick={() => addBlock("image")} data-testid={`guide-add-image-${idx}`} className="inline-flex items-center gap-1.5 font-feature text-xs uppercase tracking-widest border border-[#2A201A] px-3 py-2 hover:border-teal hover:text-teal transition-colors"><Plus className="h-3.5 w-3.5" /> Image</button>
          <button onClick={() => addBlock("video")} data-testid={`guide-add-video-${idx}`} className="inline-flex items-center gap-1.5 font-feature text-xs uppercase tracking-widest border border-[#2A201A] px-3 py-2 hover:border-teal hover:text-teal transition-colors"><Plus className="h-3.5 w-3.5" /> Video</button>
        </div>
      </div>

      <Actions onSave={() => save(it)} onUp={() => move(idx, -1)} onDown={() => move(idx, 1)} onDelete={() => remove(it.id)} />
    </div>
  );
};

const ArticlesBlock = () => {
  const { items, patch, setItem, add, save, remove, move } = useCrud("guides", { title: "New Guide", slug: "", blurb: "", cover: "", icon: "BookOpen", blocks: [] });
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-[#8F877C] text-sm">Create shareable how-to guides (camping, horse care, housing…). Each has its own page with text, images and video.</p>
        <button onClick={add} data-testid="guide-article-add" className="inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-4 py-2.5 hover:bg-bone transition-colors"><Plus className="h-4 w-4" /> Add Guide</button>
      </div>
      {items.map((it, idx) => (
        <GuideCard key={it.id} it={it} idx={idx} patch={patch} setItem={setItem} save={save} remove={remove} move={move} />
      ))}
    </div>
  );
};

export const GuideEditor = () => (
  <div className="space-y-12">
    <ArticlesBlock />
    <StepsBlock />
    <KeybindsBlock />
    <FaqBlock />
  </div>
);
